import React from 'react';
import { planetSymbols, planetColors, PlanetKey } from '../utils/planets';

type WesternChart = {
  planets?: Record<string, any>;
  houses?: Record<string, any>;
};

type VedicChart = {
  grahas?: Record<string, any>;
  bhavas?: Record<string, any>;
  core?: { lagna?: { rasi_num?: number; sign?: string } };
};

type Props = {
  mode: 'western' | 'vedic';
  western?: WesternChart | null;
  vedic?: VedicChart | null;
};

// Helper to coerce any string key to PlanetKey when present
const toPlanetKey = (name: string): PlanetKey | null => {
  const k = name.toLowerCase();
  if (k in planetSymbols) return k as PlanetKey;
  return null;
};

const ChartView: React.FC<Props> = ({ mode, western, vedic }) => {
  if (mode === 'western') {
    return <WesternWheel chart={western || {}} />;
  }
  return <VedicDiamond chart={vedic || {}} />;
};

const WesternWheel: React.FC<{ chart: WesternChart }> = ({ chart }) => {
  const size = 320;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = 150;
  const innerR = 105;
  const houseTickR = 150;
  const planets = chart.planets || {};

  const slices = Array.from({ length: 12 }, (_, i) => i);

  return (
    <div className="w-full flex items-center justify-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background circle */}
        <circle cx={cx} cy={cy} r={outerR + 6} fill="rgba(20,20,32,0.3)" stroke="rgba(255,184,79,0.25)" />
        {/* 12 houses slices */}
        {slices.map((i) => {
          const startAngle = ((i / 12) * 2 * Math.PI) - Math.PI / 2;
          const endAngle = (((i + 1) / 12) * 2 * Math.PI) - Math.PI / 2;
          const x1 = cx + innerR * Math.cos(startAngle);
          const y1 = cy + innerR * Math.sin(startAngle);
          const x2 = cx + outerR * Math.cos(startAngle);
          const y2 = cy + outerR * Math.sin(startAngle);
          const x3 = cx + outerR * Math.cos(endAngle);
          const y3 = cy + outerR * Math.sin(endAngle);
          const x4 = cx + innerR * Math.cos(endAngle);
          const y4 = cy + innerR * Math.sin(endAngle);
          const largeArc = 0;
          return (
            <path
              key={i}
              d={[
                `M ${x1} ${y1}`,
                `L ${x2} ${y2}`,
                `A ${outerR} ${outerR} 0 ${largeArc} 1 ${x3} ${y3}`,
                `L ${x4} ${y4}`,
                `A ${innerR} ${innerR} 0 ${largeArc} 0 ${x1} ${y1}`,
                'Z',
              ].join(' ')}
              fill="rgba(20,20,32,0.35)"
              stroke="rgba(255,184,79,0.2)"
            />
          );
        })}
        {/* House ticks */}
        {slices.map((i) => {
          const angle = ((i / 12) * 2 * Math.PI) - Math.PI / 2;
          const x1 = cx + (innerR - 4) * Math.cos(angle);
          const y1 = cy + (innerR - 4) * Math.sin(angle);
          const x2 = cx + houseTickR * Math.cos(angle);
          const y2 = cy + houseTickR * Math.sin(angle);
          return <line key={`t${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(255,184,79,0.25)" strokeWidth={1} />;
        })}
        {/* Planet glyphs positioned by house (rough placement) */}
        {Object.entries(planets).map(([name, pdata]) => {
          const k = toPlanetKey(name);
          if (!k) return null;
          const house = typeof pdata.house === 'number' ? pdata.house : 1;
          const pos = typeof pdata.position === 'number' ? pdata.position : 0;
          // angle within house
          const baseAngle = (((house - 1) / 12) * 2 * Math.PI) - Math.PI / 2;
          const offset = (pos / 30) * (2 * Math.PI / 12); // degrees within sign/house
          const angle = baseAngle + offset;
          const r = (innerR + outerR) / 2;
          const px = cx + r * Math.cos(angle);
          const py = cy + r * Math.sin(angle);
          const color = planetColors[k];
          return (
            <text
              key={name}
              x={px}
              y={py}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="16"
              style={{ fill: color, filter: 'drop-shadow(0 0 3px rgba(255,255,255,0.25))' }}
            >
              {planetSymbols[k]}
            </text>
          );
        })}
        {/* Inner circle */}
        <circle cx={cx} cy={cy} r={innerR - 2} fill="rgba(20,20,32,0.4)" stroke="rgba(255,184,79,0.25)" />
      </svg>
    </div>
  );
};

const VedicDiamond: React.FC<{ chart: VedicChart }> = ({ chart }) => {
  // North Indian diamond layout, 12 houses
  // We'll render a square with diagonal lines; place planets text in house boxes approx.
  const size = 320;
  const pad = 16;
  const box = size - pad * 2;
  const center = pad + box / 2;
  const grahas = chart.grahas || {};

  // Map house positions to approximate coordinates
  const houseCenters: Record<number, { x: number; y: number }> = {
    1: { x: center, y: pad + box * 0.15 },
    2: { x: pad + box * 0.72, y: pad + box * 0.28 },
    3: { x: pad + box * 0.85, y: center },
    4: { x: pad + box * 0.72, y: pad + box * 0.72 },
    5: { x: center, y: pad + box * 0.85 },
    6: { x: pad + box * 0.28, y: pad + box * 0.72 },
    7: { x: pad + box * 0.15, y: center },
    8: { x: pad + box * 0.28, y: pad + box * 0.28 },
    9: { x: pad + box * 0.45, y: pad + box * 0.45 },
    10: { x: center, y: center },
    11: { x: pad + box * 0.55, y: pad + box * 0.55 },
    12: { x: center, y: pad + box * 0.45 },
  };

  const planetEntries = Object.entries(grahas);

  return (
    <div className="w-full flex items-center justify-center">
      <svg width={size} height={size}>
        {/* Outer square */}
        <rect x={pad} y={pad} width={box} height={box} fill="rgba(20,20,32,0.35)" stroke="rgba(255,184,79,0.25)" />
        {/* Diagonals */}
        <line x1={pad} y1={center} x2={center} y2={pad} stroke="rgba(255,184,79,0.25)" />
        <line x1={center} y1={pad} x2={pad + box} y2={center} stroke="rgba(255,184,79,0.25)" />
        <line x1={pad + box} y1={center} x2={center} y2={pad + box} stroke="rgba(255,184,79,0.25)" />
        <line x1={center} y1={pad + box} x2={pad} y2={center} stroke="rgba(255,184,79,0.25)" />
        {/* Inner diamond */}
        <polygon
          points={`${center},${pad} ${pad + box},${center} ${center},${pad + box} ${pad},${center}`}
          fill="rgba(20,20,32,0.25)"
          stroke="rgba(255,184,79,0.2)"
        />
        {/* Planets placed by house */}
        {planetEntries.map(([name, g]) => {
          const house = typeof g.house === 'number' ? g.house : 1;
          const pos = houseCenters[house] || houseCenters[1];
          const k = toPlanetKey(name);
          const glyph = k ? planetSymbols[k] : '•';
          const color = k ? planetColors[k] : '#E5E7EB';
          return (
            <text
              key={name}
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="16"
              style={{ fill: color, filter: 'drop-shadow(0 0 3px rgba(255,255,255,0.25))' }}
            >
              {glyph}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

export default ChartView;


